import React from 'react';
import {
    Card,
    CardContent,
    Divider,
    CardHeader,
    TableCell,
    Table,
    TableBody,
    TableRow,
    makeStyles,
    Theme,
    Fade,
    Typography,
    Box,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import numeral from 'numeral';

import { IDCPData } from 'src/modules/models/lead/interfaces';
import { formatDate } from 'src/utils/formatDate';

interface IDCPInformation {
    webhookData: IDCPData;
    loading: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
    fontWeightMedium: {
        fontWeight: 500,
        backgroundColor: theme.palette.background.paper,
        width: 150,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
    },
    typography: {
        display: 'inline-block',
    },
    margin: {
        marginBottom: '1em',
    },
    sectionTitle: {
        fontWeight: 600,
        backgroundColor: theme.palette.action.hover,
        padding: theme.spacing(1),
    },
    labelCell: {
        fontWeight: 500,
        width: '40%',
        backgroundColor: theme.palette.background.default,
    },
    valueCell: {
        width: '60%',
    },
    link: {
        color: theme.palette.primary.main,
        wordBreak: 'break-all',
    },
    qaContainer: {
        padding: theme.spacing(1),
    },
    qaItem: {
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
    },
    qaQuestion: {
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    qaAnswer: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
}));



export const DCPInformation = ({
    webhookData,
    loading,
}: IDCPInformation) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const consumer = webhookData.consumer;
    const address = consumer?.address;
    const productInterest = webhookData.product_interest;
    const ownership = webhookData.ownership;
    const questionsAnswers = webhookData.questions_answers;
    const status = webhookData.status;

    return (
        <Card>
            <CardHeader title={t('DCPInformation.Title')} />
            <Divider />
            <Table className={classes.margin}>
                <TableBody>
                    <TableRow>
                        <TableCell
                            colSpan={2}
                            className={classes.sectionTitle}
                        >
                            {t('DCPInformation.GeneralInfo')}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.SalesOpportunityId')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {webhookData.sales_opportunity_id || '- - -'}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.DealerNo')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {webhookData.dealer_no || '- - -'}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.Language')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {webhookData.language || '- - -'}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.ModelNumber')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {webhookData.model_number || '- - -'}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.Comments')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {webhookData.comments || '- - -'}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.AssignmentDate')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {formatDate(webhookData.assignment_date)}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.StartDate')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {formatDate(webhookData.start_date)}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.CampaignName')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {webhookData.campaign_name || '- - -'}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.EventDescription')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {webhookData.event_description || '- - -'}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.IntentToBuy')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {webhookData.intent_to_buy || '- - -'}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>
                            {t('DCPInformation.LeadScore')}
                        </TableCell>
                        <Fade in={!loading} timeout={600}>
                            <TableCell className={classes.valueCell}>
                                {webhookData.lead_score || '- - -'}
                            </TableCell>
                        </Fade>
                    </TableRow>
                    {webhookData.byo_uri && (
                        <TableRow>
                            <TableCell className={classes.labelCell}>
                                {t('DCPInformation.ByoUri')}
                            </TableCell>
                            <Fade in={!loading} timeout={600}>
                                <TableCell className={classes.valueCell}>
                                    <a
                                        href={webhookData.byo_uri}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.link}
                                    >
                                        {webhookData.byo_uri}
                                    </a>
                                </TableCell>
                            </Fade>
                        </TableRow>
                    )}

                    {consumer && (
                        <>
                            <TableRow>
                                <TableCell
                                    colSpan={2}
                                    className={classes.sectionTitle}
                                >
                                    {t('DCPInformation.ConsumerInfo')}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.FirstName')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {consumer.first_name || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.LastName')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {consumer.last_name || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.CultureCode')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {consumer.culture_code || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.PhoneNo')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {consumer.phone_no || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.MobileNo')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {consumer.mobile_no || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.Email')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {consumer.email || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>

                            {address && (
                                <>
                                    <TableRow>
                                        <TableCell className={classes.labelCell}>
                                            {t('DCPInformation.Street')}
                                        </TableCell>
                                        <Fade in={!loading} timeout={600}>
                                            <TableCell className={classes.valueCell}>
                                                {address.street || '- - -'}
                                            </TableCell>
                                        </Fade>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className={classes.labelCell}>
                                            {t('DCPInformation.City')}
                                        </TableCell>
                                        <Fade in={!loading} timeout={600}>
                                            <TableCell className={classes.valueCell}>
                                                {address.city || '- - -'}
                                            </TableCell>
                                        </Fade>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className={classes.labelCell}>
                                            {t('DCPInformation.State')}
                                        </TableCell>
                                        <Fade in={!loading} timeout={600}>
                                            <TableCell className={classes.valueCell}>
                                                {address.state || '- - -'}
                                            </TableCell>
                                        </Fade>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className={classes.labelCell}>
                                            {t('DCPInformation.Country')}
                                        </TableCell>
                                        <Fade in={!loading} timeout={600}>
                                            <TableCell className={classes.valueCell}>
                                                {address.country || '- - -'}
                                            </TableCell>
                                        </Fade>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className={classes.labelCell}>
                                            {t('DCPInformation.PostalCode')}
                                        </TableCell>
                                        <Fade in={!loading} timeout={600}>
                                            <TableCell className={classes.valueCell}>
                                                {address.postal_code || '- - -'}
                                            </TableCell>
                                        </Fade>
                                    </TableRow>
                                </>
                            )}
                        </>
                    )}

                    {productInterest && (
                        <>
                            <TableRow>
                                <TableCell
                                    colSpan={2}
                                    className={classes.sectionTitle}
                                >
                                    {t('DCPInformation.ProductInterest')}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.Category')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {productInterest.category || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.Model')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {productInterest.model || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.Brand')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {productInterest.brand || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                        </>
                    )}

                    {status && status.length > 0 && (
                        <>
                            <TableRow>
                                <TableCell
                                    colSpan={2}
                                    className={classes.sectionTitle}
                                >
                                    {t('DCPInformation.Status')}
                                </TableCell>
                            </TableRow>
                            {status.map((s, index) => (
                                <React.Fragment key={index}>
                                    <TableRow>
                                        <TableCell className={classes.labelCell}>
                                            {t('DCPInformation.StatusCode')}
                                        </TableCell>
                                        <Fade in={!loading} timeout={600}>
                                            <TableCell className={classes.valueCell}>
                                                {s.code || '- - -'}
                                            </TableCell>
                                        </Fade>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className={classes.labelCell}>
                                            {t('DCPInformation.SetOn')}
                                        </TableCell>
                                        <Fade in={!loading} timeout={600}>
                                            <TableCell className={classes.valueCell}>
                                                {formatDate(s.set_on)}
                                            </TableCell>
                                        </Fade>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </>
                    )}

                    {ownership && (
                        <>
                            <TableRow>
                                <TableCell
                                    colSpan={2}
                                    className={classes.sectionTitle}
                                >
                                    {t('DCPInformation.Ownership')}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.OwnershipBrand')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {ownership.brand || '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell}>
                                    {t('DCPInformation.TradeIn')}
                                </TableCell>
                                <Fade in={!loading} timeout={600}>
                                    <TableCell className={classes.valueCell}>
                                        {ownership.trade_in
                                            ? numeral(ownership.trade_in).format('$0,0')
                                            : '- - -'}
                                    </TableCell>
                                </Fade>
                            </TableRow>
                        </>
                    )}

                    {questionsAnswers && questionsAnswers.length > 0 && (
                        <>
                            <TableRow>
                                <TableCell
                                    colSpan={2}
                                    className={classes.sectionTitle}
                                >
                                    {t('DCPInformation.QuestionsAnswers')}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <Box className={classes.qaContainer}>
                                        {questionsAnswers.map((qa, index) => (
                                            <Box
                                                key={index}
                                                className={classes.qaItem}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    className={classes.qaQuestion}
                                                >
                                                    {qa.question}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    className={classes.qaAnswer}
                                                >
                                                    {qa.answer}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </>
                    )}
                </TableBody>
            </Table>
        </Card>
    );
};
